import { withData, WithDataDef } from './base-composer';
import { withCompanyContacts, WithCompanyContactsDef } from './company-contacts/company-contacts';
import { GeoTargetLocation } from './tim-elements/target-location';

function isMatchingObject(obj1: any, obj2: any) {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
}

export interface TimLocation {
  lig_geography: Record<string, any>[];
}

export interface WithCurrentLocations {
  current_geography: TimLocation;
}

export interface WithTargetLocations {
  target_geography: TimLocation;
}

export interface WithTimLocations extends WithTargetLocations, WithCurrentLocations {}

export interface WithLigGeoLocationsDef extends WithTimLocations {
  generateLocationId(division: Record<string, any>): string;
  addDivision(division: Record<string, any>, field: string): void;
  deleteDivision(division, field: string);
  addTargetLocation(division: Record<string, any>): void;
  deleteTargetLocation(division: Record<string, any>): void;
  addCurrentLocation(division: Record<string, any>): void;
  deleteCurrentLocation(division: Record<string, any>): void;
}

/**
 * Composer to add /delete Region/Division
 */
export const withLigGeoLocations = (): WithLigGeoLocationsDef => {
  function addTargetLocation(division) {
    addDivision.call(this, division, 'target_geography');
  }

  function addCurrentLocation(division) {
    addDivision.call(this, division, 'current_geography');
  }

  function addDivision(division, field) {
    this[field].lig_geography = !this[field].lig_geography ? [division] : [...this[field].lig_geography, division];
  }

  function deleteTargetLocation(division) {
    deleteDivision.call(this, division, 'target_geography');
  }

  function deleteCurrentLocation(division) {
    deleteDivision.call(this, division, 'current_geography');
  }

  function deleteDivision(
    { id, global_region_desc, division_desc, market_desc, submarket_desc, district_desc, neighborhood_desc },
    field
  ) {
    const division = {
      id,
      global_region_desc,
      division_desc,
      market_desc,
      submarket_desc,
      district_desc,
      neighborhood_desc,
    };
    this[field].lig_geography.splice(
      this[field].lig_geography.findIndex(element =>
        isMatchingObject(
          {
            id: element.id,
            global_region_desc: element.global_region_desc,
            division_desc: element.division_desc,
            market_desc: element.market_desc,
            submarket_desc: element.submarket_desc,
            district_desc: element.district_desc,
            neighborhood_desc: element.neighborhood_desc,
          },
          division
        )
      ),
      1
    );
  }

  function generateLocationId({
    global_region_desc,
    region_desc,
    division_desc,
    market_desc,
    submarket_desc,
    district_desc,
    neighborhood_desc,
  }) {
    return [
      global_region_desc,
      division_desc ? division_desc : region_desc,
      market_desc,
      submarket_desc,
      district_desc,
      neighborhood_desc,
    ]
      .filter(x => !!x)
      .join('.');
  }

  return {
    addDivision,
    addTargetLocation,
    addCurrentLocation,
    deleteDivision,
    deleteCurrentLocation,
    deleteTargetLocation,
    generateLocationId,
  } as WithLigGeoLocationsDef;
};

/**
 * Class definition for Tim composer
 */
export interface TimDef extends WithDataDef, WithCompanyContactsDef, WithCurrentLocations, WithTargetLocations {
  [key: string]: any;
}
/**
 * Tim composer function
 * @param state
 * Supports New Geos structure
 */
export const Tim = (state): TimDef => {
  const extendedState = Object.assign(
    {
      target_geography: GeoTargetLocation(state.target_geography),
      current_geography: GeoTargetLocation(state.current_geography),
    },
    // composers
    withData(),
    withLigGeoLocations(),
    withCompanyContacts()
  );
  return Object.assign({}, state, extendedState);
};
